import React, { Suspense, createContext, useContext } from 'react'
import { CartItem, CartObject } from '../../../cart/storage/cart.types'
import { Product } from '../../../products/storage'

import { useSelector } from 'react-redux'
import { selectContactInfo } from '../../../azure-b2c/storage'
import EnquiryModal from '../EnquireModal/EnquireModal'
import { selectConfigByKey } from '../../../../services/firebase/remote-config/remoteConfig.slice'
import { SendOfferModal } from '../QuoteDetails/SendOfferModal'
import { QuoteModalType } from './types'

const QuoteModal = React.lazy(() => import('./QuoteModal'))

type QuoteModalProductType = Product | CartItem

export const QuoteModalContext = createContext<{
  // False if no fullName property on relationship Owner
  canRequestQuote: boolean
  product: QuoteModalProductType[] | null
  quoteType: QuoteModalType
  setProduct: (item: QuoteModalProductType | QuoteModalProductType[] | null) => void | null
  setQuoteType: (type: QuoteModalType) => void
  isQuoteable: (product: Product | CartItem) => boolean
  isCartQuotable: (cart: CartObject) => boolean
  quoteModalFromCart: (cart: CartObject, isCold?: boolean) => void
  cartIsOverValue: (value: number) => boolean
}>({
  canRequestQuote: false,
  product: null,
  quoteType: QuoteModalType.QUOTE,
  setProduct: () => null,
  setQuoteType: () => null,
  isQuoteable: () => false,
  cartIsOverValue: () => false,
  isCartQuotable: () => {
    console.log('Not implemented')
    return false
  },
  quoteModalFromCart: () => null,
})

export const useQuoteModal = () => {
  const context = useContext(QuoteModalContext)

  if (!context) {
    console.error('useQuoteModal must be used within a QuoteProvider')
    throw new Error('useQuoteModal must be used within a QuoteProvider')
  }

  return context
}

export const QuoteProvider = (Component: () => JSX.Element) => {
  const relationshipOwner = useSelector(selectContactInfo)
  const canRequestQuote = Object.hasOwn(relationshipOwner, 'fullName')

  const [product, setProduct] = React.useState<QuoteModalProductType[] | null>(null)
  const [quoteType, setQuoteType] = React.useState<QuoteModalType>(QuoteModalType.QUOTE)

  const setProductCallback = (item: QuoteModalProductType | QuoteModalProductType[] | null) => {
    // if item is not null and not an array
    if (item !== null && !Array.isArray(item)) {
      setProduct([item])
    } else {
      setProduct(item)
    }
  }

  const minQuoteValue: number = useSelector(selectConfigByKey('quotePriceMin'))
  const quoteEnabled: boolean = useSelector(selectConfigByKey('quoteEnabled'))
  const isQuoteable = (item: Product | CartItem): boolean => {
    return !!(
      quoteEnabled &&
      canRequestQuote &&
      item.isPriceNegotiable &&
      !product?.find((a) => a?.productId === item.productId)
    )
  }

  // Cart total cost is over $10k in value
  const cartIsOverValue = (value: number) => {
    return quoteEnabled && value > minQuoteValue
  }

  const quoteModalFromCart = (cart: CartObject, isCold = false) => {
    if (!quoteEnabled) return
    setProduct(cart.items)
    if (isCold) {
      setQuoteType(QuoteModalType.OFFER)
    } else {
      setQuoteType(QuoteModalType.QUOTE)
    }
  }
  const isCartQuotable = React.useCallback((cart: CartObject) => {
    return (
      quoteEnabled &&
      canRequestQuote &&
      cart.items.some((cartItem) => isQuoteable(cartItem)) &&
      cartIsOverValue(cart.totalAmountIncGst)
    )
  }, [])

  const value = React.useMemo(() => {
    return {
      canRequestQuote,
      product,
      quoteType,
      setProduct: setProductCallback,
      setQuoteType,
      isQuoteable,
      isCartQuotable,
      quoteModalFromCart,
      cartIsOverValue,
    }
  }, [
    quoteEnabled,
    canRequestQuote,
    product,
    setProductCallback,
    quoteType,
    setQuoteType,
    isCartQuotable,
    isQuoteable,
    cartIsOverValue,
  ])

  const getModal = React.useMemo(() => {
    if (quoteType === QuoteModalType.OFFER) {
      return (
        <Suspense>
          <SendOfferModal
            isModalOpen={quoteType === QuoteModalType.OFFER && !!product}
            products={product as CartItem[]}
            setIsModalOpen={(val) => {
              if (!val) {
                setProduct(null)
                setQuoteType
              }
            }}
            cold
          />
        </Suspense>
      )
    }

    if (quoteType === QuoteModalType.ENQUIRY && product) {
      return (
        <Suspense>
          <EnquiryModal />
        </Suspense>
      )
    }
    if (quoteType === QuoteModalType.QUOTE && canRequestQuote) {
      return (
        <Suspense>
          <QuoteModal />
        </Suspense>
      )
    }
  }, [canRequestQuote, quoteType, product])

  return (
    <QuoteModalContext.Provider value={value}>
      {Component()}
      {getModal}
    </QuoteModalContext.Provider>
  )
}

export default QuoteProvider
